/*
 * Copyright (c) 2026-present Amida Technology Solutions, Inc.
 * All rights reserved.
 *
 * Licensed under the Amida proprietary license.
 * See LICENSE file in the project root for license information.
 */

import { useEffect, useEffectEvent, useRef } from 'react';
import { createPortal } from 'react-dom';

/** What can take focus inside the dialog, in tab order. */
const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[contenteditable="true"]',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

/**
 * A dialog over a scrim.
 *
 * Escape closes it and focus moves inside on open, because a dialog you can
 * reach with the keyboard and not leave is worse than no dialog. While it is
 * open, Tab and Shift+Tab go round the dialog rather than out to the page
 * hidden behind the scrim, and when it closes focus goes back to whatever
 * opened it.
 *
 * Focus moves in once, when it opens. A caller that passes a new `onClose` on
 * every render -- a page polling underneath it -- must not pull focus out of
 * the field somebody is typing in, so `onClose` is read when Escape is
 * pressed, not watched.
 */
export function Modal({
  title,
  onClose,
  children,
  footer,
  wide = false,
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
  footer?: React.ReactNode;
  /** For a document: as wide as a page, not as wide as a form. */
  wide?: boolean;
}) {
  const panel = useRef<HTMLDivElement>(null);

  const closeNow = useEffectEvent(() => {
    onClose();
  });

  useEffect(() => {
    // Where focus was, to give it back when the dialog goes.
    const opener =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    panel.current?.focus();

    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closeNow();
        return;
      }
      if (event.key !== 'Tab' || !panel.current) return;
      const inside = Array.from(panel.current.querySelectorAll<HTMLElement>(FOCUSABLE));
      const first = inside[0];
      const last = inside.at(-1);
      const here = document.activeElement;
      if (!first || !last) {
        event.preventDefault();
        panel.current.focus();
      } else if (event.shiftKey && (here === first || here === panel.current)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && here === last) {
        event.preventDefault();
        first.focus();
      } else if (!panel.current.contains(here)) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      // Back where it was, if that is still on the page to take it.
      if (opener?.isConnected) opener.focus();
    };
  }, []);

  return createPortal(
    <>
      <div className="scrim" onClick={onClose} aria-hidden />
      <div
        className={wide ? 'modal modal-wide' : 'modal'}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        ref={panel}
      >
        <div className="modal-h">
          <h2>{title}</h2>
        </div>
        <div className="modal-b">{children}</div>
        {footer ? <div className="modal-f">{footer}</div> : null}
      </div>
    </>,
    document.body,
  );
}
